import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, CircularProgress } from '@mui/material';

const PrivateRoute = ({ children, requireSubscription = false }) => {
    const { user, isLoading, isError } = useSelector((state) => state.auth);
    const location = useLocation();

    // Check if user is authenticated and has a valid token
    const isAuthenticated = user && user.token && !isError;

    // Show loading spinner while auth state is being determined
    if (isLoading) {
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh' 
                }}
            >
                <CircularProgress />
            </Box>
        );
    }
    
    // Handle unauthenticated users
    if (!isAuthenticated) {
        toast.error('Please log in to access this page', { toastId: 'auth-required' });
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Handle unverified users
    if (user && !user.verified) {
        toast.warning('Please verify your email to continue', { toastId: 'verify-required' });
        return <Navigate to="/email-verification-status" replace />;
    }

    // Handle subscription requirement if specified
    if (requireSubscription && user && !user.hasActiveSubscription) {
        toast.info('Please subscribe to access this feature', { toastId: 'subscription-required' });
        return <Navigate to="/pricing" replace />;
    }

    // User is authenticated and verified - render the protected content
    return children ? children : <Outlet />;
};

export default PrivateRoute;